import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowRight, Briefcase, Wrench, Home, Stethoscope, Scale, Heart, Car, Scissors, Dumbbell, Trees, Building, Truck, Paintbrush, Calculator, TrendingUp, Zap, Lock, Flower, UtensilsCrossed, Camera, Shield, Droplets, Bug, Hammer, Wind, Trash2, Waves, Axe, DoorClosed, Palette, Laptop, CarFront, Gem, Sun, Dog, UserCheck, Music, BookOpen, Sparkle, Bike, Activity, Baby, Users, PersonStanding, Eye, Shirt, Plane, Sailboat, Bike as Motorcycle, Flame, Sofa, Target, Sparkles, Building2, Fence, HomeIcon, TrendingDown, FileText, KeyRound, Store, Calendar } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';

const IndustriesPage = () => {
  const { formatPrice } = useCurrency();

  // Scroll to top helper
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const industries = [
    {
      id: 'plumber',
      name: 'Plumbing Services',
      icon: Wrench,
      description: 'Capture emergency calls 24/7, send instant quotes, and turn one-time customers into loyal repeat clients',
      route: '/industries/plumber',
      color: 'bg-blue-500'
    },
    {
      id: 'electrician',
      name: 'Electrical Services',
      icon: Zap,
      description: 'Never miss an emergency call, automate scheduling, and build a pipeline of recurring maintenance clients',
      route: '/industries/electrician',
      color: 'bg-yellow-500'
    },
    {
      id: 'hvac',
      name: 'HVAC Services',
      icon: Home,
      description: 'Automate seasonal maintenance reminders, capture emergency calls, and fill your calendar year-round',
      route: '/industries/hvac',
      color: 'bg-orange-500'
    },
    {
      id: 'roofing',
      name: 'Roofing Services',
      icon: Home,
      description: 'Generate quality leads, automate follow-up on estimates, and manage large project pipelines',
      route: '/industries/roofing',
      color: 'bg-red-500'
    },
    {
      id: 'landscaping',
      name: 'Landscaping Services',
      icon: Trees,
      description: 'Automate seasonal reminders, manage recurring maintenance clients, and grow your contract base',
      route: '/industries/landscaping',
      color: 'bg-green-500'
    },
    {
      id: 'dental',
      name: 'Dental Practice',
      icon: Stethoscope,
      description: 'Reduce no-shows, automate appointment reminders, and get more 5-star reviews from happy patients',
      route: '/industries/dental',
      color: 'bg-teal-500'
    },
    {
      id: 'chiropractor',
      name: 'Chiropractic Care',
      icon: Heart,
      description: 'Automate patient intake, reduce no-shows with reminders, and build long-term treatment relationships',
      route: '/industries/chiropractor',
      color: 'bg-purple-500'
    },
    {
      id: 'hair-salon',
      name: 'Hair Salon & Barber',
      icon: Scissors,
      description: 'Fill last-minute cancellations, automate rebooking, and turn one-time clients into regulars',
      route: '/industries/hair-salon',
      color: 'bg-pink-500'
    },
    {
      id: 'personal-trainer',
      name: 'Personal Training',
      icon: Dumbbell,
      description: 'Automate client check-ins, manage class bookings, and keep clients engaged between sessions',
      route: '/industries/personal-trainer',
      color: 'bg-indigo-500'
    },
    {
      id: 'real-estate',
      name: 'Real Estate Agent',
      icon: Building,
      description: 'Never miss a buyer/seller lead, automate property alerts, and nurture leads until they are ready',
      route: '/industries/real-estate-agent',
      color: 'bg-cyan-500'
    },
    {
      id: 'auto-repair',
      name: 'Auto Repair Shop',
      icon: Car,
      description: 'Automate service reminders, capture breakdown calls instantly, and build a loyal customer base',
      route: '/industries/automotive-repair',
      color: 'bg-gray-600'
    },
    {
      id: 'auto-body',
      name: 'Auto Body & Paint',
      icon: Paintbrush,
      description: 'Follow up on insurance estimates automatically, manage complex repair jobs, and get more referrals',
      route: '/industries/auto-body-painting',
      color: 'bg-slate-600'
    },
    {
      id: 'towing',
      name: 'Towing Service',
      icon: Truck,
      description: 'Capture emergency tow requests 24/7, dispatch efficiently, and turn emergencies into recurring customers',
      route: '/industries/towing-service',
      color: 'bg-amber-600'
    },
    {
      id: 'personal-injury',
      name: 'Personal Injury Attorney',
      icon: Scale,
      description: 'Capture urgent case leads instantly, automate case status updates, and nurture potential clients',
      route: '/industries/personal-injury-attorney',
      color: 'bg-blue-700'
    },
    {
      id: 'accounting',
      name: 'Accounting Firm',
      icon: Calculator,
      description: 'Automate tax season reminders, manage client onboarding, and reduce back-and-forth communications',
      route: '/industries/accounting-firm',
      color: 'bg-emerald-600'
    },
    {
      id: 'marketing',
      name: 'Marketing Agency',
      icon: TrendingUp,
      description: 'Automate lead nurturing, manage client projects, and showcase ROI with automated reporting',
      route: '/industries/marketing-agency',
      color: 'bg-violet-600'
    },
    {
      id: 'veterinary',
      name: 'Veterinary Clinic',
      icon: Heart,
      description: 'Capture emergency pet calls 24/7, automate appointment reminders, and turn visits into lifelong clients',
      route: '/industries/veterinary',
      color: 'bg-rose-500'
    },
    {
      id: 'cleaning',
      name: 'Cleaning Service',
      icon: Wrench,
      description: 'Automate booking, convert one-time cleans to recurring contracts, and fill your schedule consistently',
      route: '/industries/cleaning-service',
      color: 'bg-sky-500'
    },
    {
      id: 'pest-control',
      name: 'Pest Control',
      icon: Bug,
      description: 'Capture emergency calls, automate treatment reminders, and build recurring maintenance contracts',
      route: '/industries/pest-control',
      color: 'bg-lime-600'
    },
    {
      id: 'locksmith',
      name: 'Locksmith Service',
      icon: Lock,
      description: 'Never miss emergency lockout calls, dispatch faster, and convert to security upgrade clients',
      route: '/industries/locksmith',
      color: 'bg-stone-600'
    },
    {
      id: 'massage',
      name: 'Massage Therapy',
      icon: Heart,
      description: 'Fill cancellations, automate rebooking, and build a loyal base of weekly regular clients',
      route: '/industries/massage-therapy',
      color: 'bg-fuchsia-500'
    },
    {
      id: 'home-inspector',
      name: 'Home Inspector',
      icon: Home,
      description: 'Capture realtor referrals, automate report delivery, and build strong partnership networks',
      route: '/industries/home-inspector',
      color: 'bg-amber-500'
    },
    {
      id: 'insurance',
      name: 'Insurance Agency',
      icon: Shield,
      description: 'Nurture leads until they buy, automate renewals, and increase policy cross-sells by 40%',
      route: '/industries/insurance-agency',
      color: 'bg-blue-600'
    },
    {
      id: 'florist',
      name: 'Florist',
      icon: Flower,
      description: 'Automate holiday reminders, manage rush orders, and create recurring subscription revenue',
      route: '/industries/florist',
      color: 'bg-pink-400'
    },
    {
      id: 'catering',
      name: 'Catering Service',
      icon: UtensilsCrossed,
      description: 'Capture event inquiries 24/7, automate menu selection, and manage bookings efficiently',
      route: '/industries/catering',
      color: 'bg-orange-600'
    },
    {
      id: 'pet-grooming',
      name: 'Pet Grooming',
      icon: Scissors,
      description: 'Reduce no-shows, automate grooming reminders, and turn one-time visits into regulars',
      route: '/industries/pet-grooming',
      color: 'bg-teal-400'
    },
    {
      id: 'appliance-repair',
      name: 'Appliance Repair',
      icon: Wrench,
      description: 'Capture emergency breakdowns, schedule efficiently, and build preventive maintenance contracts',
      route: '/industries/appliance-repair',
      color: 'bg-gray-500'
    },
    {
      id: 'contractor',
      name: 'General Contractor',
      icon: Hammer,
      description: 'Convert more estimates, automate project updates, and manage multiple projects without chaos',
      route: '/industries/general-contractor',
      color: 'bg-yellow-700'
    },
    {
      id: 'carpet-cleaning',
      name: 'Carpet Cleaning',
      icon: Droplets,
      description: 'Book more jobs, automate seasonal reminders, and convert to annual cleaning contracts',
      route: '/industries/carpet-cleaning',
      color: 'bg-blue-400'
    },
    {
      id: 'moving',
      name: 'Moving Company',
      icon: Truck,
      description: 'Capture last-minute requests, automate estimates, and fill your truck schedule faster',
      route: '/industries/moving-company',
      color: 'bg-indigo-600'
    },
    {
      id: 'photography',
      name: 'Photography',
      icon: Camera,
      description: 'Book more sessions, automate gallery delivery, and get 70% of clients to rebook',
      route: '/industries/photography',
      color: 'bg-purple-600'
    },
    {
      id: 'window-cleaning',
      name: 'Window Cleaning',
      icon: Wind,
      description: 'Build year-round recurring revenue, capture seasonal rush, and get commercial contracts',
      route: '/industries/window-cleaning',
      color: 'bg-sky-400'
    },
    {
      id: 'junk-removal',
      name: 'Junk Removal',
      icon: Trash2,
      description: 'Capture same-day requests, optimize truck routes, and turn one-time jobs into recurring cleanouts',
      route: '/industries/junk-removal',
      color: 'bg-stone-500'
    },
    {
      id: 'pool-service',
      name: 'Pool Service',
      icon: Waves,
      description: 'Build recurring route revenue, automate seasonal services, and never miss a service window',
      route: '/industries/pool-service',
      color: 'bg-cyan-400'
    },
    {
      id: 'handyman',
      name: 'Handyman Service',
      icon: Hammer,
      description: 'Stop playing phone tag, build recurring clients, and convert to honey-do list subscriptions',
      route: '/industries/handyman',
      color: 'bg-orange-500'
    },
    {
      id: 'tree-service',
      name: 'Tree Service',
      icon: Axe,
      description: 'Capture storm emergency calls, build recurring maintenance, and close large project estimates',
      route: '/industries/tree-service',
      color: 'bg-emerald-700'
    },
    {
      id: 'pressure-washing',
      name: 'Pressure Washing',
      icon: Droplets,
      description: 'Build annual contracts, automate seasonal reminders, and get commercial recurring revenue',
      route: '/industries/pressure-washing',
      color: 'bg-blue-500'
    },
    {
      id: 'lawn-care',
      name: 'Lawn Care',
      icon: Trees,
      description: 'Stop chasing payments, optimize routes, and build predictable recurring revenue',
      route: '/industries/lawn-care',
      color: 'bg-green-600'
    },
    {
      id: 'garage-door',
      name: 'Garage Door Repair',
      icon: DoorClosed,
      description: 'Never miss emergency calls, build maintenance revenue, and convert repairs to new sales',
      route: '/industries/garage-door',
      color: 'bg-slate-600'
    },
    {
      id: 'painting',
      name: 'Painting Contractor',
      icon: Palette,
      description: 'Stop losing estimates, book crew 8 weeks out, and get 50% more conversions',
      route: '/industries/painting',
      color: 'bg-rose-400'
    },
    {
      id: 'it-services',
      name: 'IT Services',
      icon: Laptop,
      description: 'Capture emergency calls, convert break-fix to MSP, and build predictable recurring revenue',
      route: '/industries/it-services',
      color: 'bg-indigo-700'
    },
    {
      id: 'auto-detailing',
      name: 'Auto Detailing',
      icon: CarFront,
      description: 'Fill bay schedule, build monthly memberships, and secure fleet detailing contracts',
      route: '/industries/auto-detailing',
      color: 'bg-gray-700'
    },
    {
      id: 'wedding-planner',
      name: 'Wedding Planner',
      icon: Gem,
      description: 'Book more weddings, automate vendor coordination, and manage events stress-free',
      route: '/industries/wedding-planner',
      color: 'bg-pink-300'
    },
    {
      id: 'solar',
      name: 'Solar Installation',
      icon: Sun,
      description: 'Nurture leads through long sales cycles, track ROI calculations, and close more deals',
      route: '/industries/solar-installation',
      color: 'bg-yellow-500'
    },
    {
      id: 'dog-training',
      name: 'Dog Training',
      icon: Dog,
      description: 'Fill group classes, build long-term clients, and convert to advanced training programs',
      route: '/industries/dog-training',
      color: 'bg-amber-700'
    },
    {
      id: 'yoga',
      name: 'Yoga Studio',
      icon: UserCheck,
      description: 'Fill classes consistently, reduce member churn, and grow membership on autopilot',
      route: '/industries/yoga-studio',
      color: 'bg-purple-400'
    },
    {
      id: 'martial-arts',
      name: 'Martial Arts School',
      icon: Dumbbell,
      description: 'Keep students engaged, reduce dropouts, and grow to black belt level',
      route: '/industries/martial-arts',
      color: 'bg-red-700'
    },
    {
      id: 'music-lessons',
      name: 'Music Lessons',
      icon: Music,
      description: 'Fill teaching schedule, reduce dropouts, and keep students progressing long-term',
      route: '/industries/music-lessons',
      color: 'bg-indigo-400'
    },
    {
      id: 'tutoring',
      name: 'Tutoring Service',
      icon: BookOpen,
      description: 'Book more sessions, track student progress, and build long-term relationships',
      route: '/industries/tutoring',
      color: 'bg-blue-700'
    },
    {
      id: 'medspa',
      name: 'Medspa & Aesthetics',
      icon: Sparkle,
      description: 'Fill appointment book, build treatment plan revenue, and convert consultations to packages',
      route: '/industries/medspa-aesthetics',
      color: 'bg-fuchsia-400'
    },
    {
      id: 'bike-shop',
      name: 'Bike Shop',
      icon: Bike,
      description: 'Increase service revenue, build cycling community, and capture year-round sales',
      route: '/industries/bike-shop',
      color: 'bg-green-700'
    },
    {
      id: 'physical-therapy',
      name: 'Physical Therapy',
      icon: Activity,
      description: 'Fill schedule, improve patient compliance, and track treatment progress effortlessly',
      route: '/industries/physical-therapy',
      color: 'bg-blue-600'
    },
    {
      id: 'childcare',
      name: 'Childcare & Daycare',
      icon: Baby,
      description: 'Fill enrollment, automate parent communication, and build thriving childcare community',
      route: '/industries/childcare',
      color: 'bg-yellow-400'
    },
    {
      id: 'senior-care',
      name: 'Senior Care',
      icon: Users,
      description: 'Build trust with families, streamline caregiver scheduling, and grow home care agency',
      route: '/industries/senior-care',
      color: 'bg-purple-500'
    },
    {
      id: 'dance-studio',
      name: 'Dance Studio',
      icon: PersonStanding,
      description: 'Fill classes, reduce dropouts, and manage recitals without chaos',
      route: '/industries/dance-studio',
      color: 'bg-pink-500'
    },
    {
      id: 'optometry',
      name: 'Optometry',
      icon: Eye,
      description: 'Fill schedule, increase eyewear sales, and improve annual exam compliance',
      route: '/industries/optometry',
      color: 'bg-cyan-600'
    },
    {
      id: 'tattoo-shop',
      name: 'Tattoo Shop',
      icon: Paintbrush,
      description: 'Book more sessions, manage multi-session projects, and build loyal client base',
      route: '/industries/tattoo-shop',
      color: 'bg-slate-700'
    },
    {
      id: 'nail-salon',
      name: 'Nail Salon',
      icon: Sparkle,
      description: 'Fill every chair, turn clients into weekly regulars, and reduce no-shows',
      route: '/industries/nail-salon',
      color: 'bg-rose-300'
    },
    {
      id: 'spa-services',
      name: 'Day Spa',
      icon: Heart,
      description: 'Fill treatment rooms, build membership revenue, and convert visitors to members',
      route: '/industries/spa-services',
      color: 'bg-purple-300'
    },
    {
      id: 'gymnastics',
      name: 'Gymnastics Center',
      icon: Dumbbell,
      description: 'Fill classes, progress students to competitive teams, and grow thriving programs',
      route: '/industries/gymnastics',
      color: 'bg-red-600'
    },
    {
      id: 'swimming-lessons',
      name: 'Swim School',
      icon: Waves,
      description: 'Fill classes year-round, progress students through levels, and manage waitlists',
      route: '/industries/swimming-lessons',
      color: 'bg-blue-400'
    },
    {
      id: 'counseling',
      name: 'Counseling & Therapy',
      icon: Heart,
      description: 'Fill practice, reduce no-shows by 50%, and manage intake efficiently',
      route: '/industries/counseling',
      color: 'bg-green-500'
    },
    {
      id: 'podiatry',
      name: 'Podiatry',
      icon: Activity,
      description: 'Fill schedule, improve diabetic compliance, and reduce no-shows',
      route: '/industries/podiatry',
      color: 'bg-teal-600'
    },
    {
      id: 'barber-shop',
      name: 'Barber Shop',
      icon: Scissors,
      description: 'Keep chairs full, build weekly regulars, and book weeks in advance',
      route: '/industries/barber-shop',
      color: 'bg-amber-800'
    },
    {
      id: 'tennis-lessons',
      name: 'Tennis Lessons',
      icon: Dumbbell,
      description: 'Fill court time, build long-term students, and track skill progression',
      route: '/industries/tennis-lessons',
      color: 'bg-lime-500'
    },
    {
      id: 'golf-lessons',
      name: 'Golf Instruction',
      icon: Dumbbell,
      description: 'Book more lessons, build coaching relationships, and convert trials to packages',
      route: '/industries/golf-lessons',
      color: 'bg-green-600'
    },
    {
      id: 'boat-repair',
      name: 'Boat Repair',
      icon: Sailboat,
      description: 'Capture pre-season rush, build year-round maintenance, and manage complex projects',
      route: '/industries/boat-repair',
      color: 'bg-blue-700'
    },
    {
      id: 'rv-repair',
      name: 'RV Repair',
      icon: Truck,
      description: 'Capture pre-trip calls, build recurring maintenance, and manage service projects',
      route: '/industries/rv-repair',
      color: 'bg-orange-700'
    },
    {
      id: 'motorcycle-repair',
      name: 'Motorcycle Repair',
      icon: Motorcycle,
      description: 'Capture riding season rush, build loyal community, and manage custom builds',
      route: '/industries/motorcycle-repair',
      color: 'bg-gray-800'
    },
    {
      id: 'firewood',
      name: 'Firewood Delivery',
      icon: Flame,
      description: 'Automate seasonal orders, optimize delivery routes, and build recurring customers',
      route: '/industries/firewood',
      color: 'bg-orange-800'
    },
    {
      id: 'upholstery',
      name: 'Upholstery & Furniture Repair',
      icon: Sofa,
      description: 'Stop losing estimates, showcase work quality, and book restoration projects',
      route: '/industries/upholstery',
      color: 'bg-brown-600'
    },
    {
      id: 'acupuncture',
      name: 'Acupuncture Clinic',
      icon: Target,
      description: 'Fill practice, build wellness clients, and convert to treatment series',
      route: '/industries/acupuncture',
      color: 'bg-teal-500'
    },
    {
      id: 'dermatology',
      name: 'Dermatology',
      icon: Sparkles,
      description: 'Fill schedule, increase cosmetic procedures, and grow skincare revenue',
      route: '/industries/dermatology',
      color: 'bg-pink-600'
    },
    {
      id: 'pet-sitting',
      name: 'Pet Sitting & Dog Walking',
      icon: Dog,
      description: 'Build recurring clients, automate updates, and fill daily schedule',
      route: '/industries/pet-sitting',
      color: 'bg-amber-600'
    },
    {
      id: 'fence-installation',
      name: 'Fence Installation',
      icon: Fence,
      description: 'Convert more estimates, showcase work, and book months ahead',
      route: '/industries/fence-installation',
      color: 'bg-stone-700'
    },
    {
      id: 'chimney-sweep',
      name: 'Chimney Sweep',
      icon: HomeIcon,
      description: 'Capture pre-winter rush, build annual service revenue, and automate reminders',
      route: '/industries/chimney-sweep',
      color: 'bg-gray-700'
    },
    {
      id: 'deck-builder',
      name: 'Deck Building',
      icon: Building2,
      description: 'Convert estimates, showcase projects, and book year-round',
      route: '/industries/deck-builder',
      color: 'bg-amber-700'
    },
    {
      id: 'septic-service',
      name: 'Septic Tank Service',
      icon: Truck,
      description: 'Capture emergencies, automate pumping reminders, and optimize routes',
      route: '/industries/septic-service',
      color: 'bg-brown-700'
    },
    {
      id: 'gutter-cleaning',
      name: 'Gutter Cleaning',
      icon: Droplets,
      description: 'Capture seasonal rush, build recurring revenue, and optimize routes',
      route: '/industries/gutter-cleaning',
      color: 'bg-cyan-700'
    },
    {
      id: 'tax-preparation',
      name: 'Tax Preparation',
      icon: FileText,
      description: 'Build year-round clients, automate reminders, and increase advisory revenue',
      route: '/industries/tax-preparation',
      color: 'bg-green-800'
    },
    {
      id: 'property-management',
      name: 'Property Management',
      icon: KeyRound,
      description: 'Scale portfolio, reduce owner turnover, and automate communications',
      route: '/industries/property-management',
      color: 'bg-indigo-800'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0E163B] via-[#1a2654] to-[#0E163B] text-white py-24 px-4 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FF5A3D] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FF8C42] rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-500 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-6 py-2 mb-8">
            <Briefcase size={20} className="text-[#FF5A3D]" />
            <span className="text-sm font-semibold">81+ Industries • Pre-Built Templates • 5-Min Setup</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Your Industry.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF5A3D] via-[#FF8C42] to-[#FFB84D]">
              Your Growth System.
            </span>
          </h1>

          {/* Subheading */}
          <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-4xl mx-auto leading-relaxed">
            Stop using generic tools built for everyone. Get industry-specific automation, templates, and workflows that actually understand <span className="text-[#FF8C42] font-semibold">how your business works</span>.
          </p>

          {/* Social Proof Chips */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-2 flex items-center gap-2">
              <div className="text-green-400 font-bold">✓</div>
              <span className="text-sm">No Technical Skills Required</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-2 flex items-center gap-2">
              <div className="text-green-400 font-bold">✓</div>
              <span className="text-sm">Ready in 5 Minutes</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-2 flex items-center gap-2">
              <div className="text-green-400 font-bold">✓</div>
              <span className="text-sm">Cancel Anytime</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              asChild
              className="bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] hover:from-[#E5512E] hover:to-[#E57A35] text-white font-bold text-lg px-10 py-7 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-[#FF5A3D]/50"
            >
              <Link to="/pricing" onClick={scrollToTop}>
                <span className="flex items-center gap-2">
                  Start Your {formatPrice(5)} Trial
                  <ArrowRight size={20} />
                </span>
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-white/30 bg-white/5 backdrop-blur-sm text-white hover:bg-white hover:text-[#0E163B] font-semibold text-lg px-10 py-7 rounded-xl transition-all duration-300"
            >
              <Link to="/contact">
                Talk to Our Team
              </Link>
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="mt-12 text-sm text-gray-400">
            <p>Join 5,247+ UK businesses already growing with Local Business Heroes</p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-gray-50 to-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Briefcase size={32} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-[#0E163B] mb-2">81+</div>
              <div className="text-gray-600 font-medium">Industries Covered</div>
              <div className="text-xs text-gray-500 mt-1">From plumbers to dentists</div>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Zap size={32} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-[#0E163B] mb-2">5 min</div>
              <div className="text-gray-600 font-medium">Setup Time</div>
              <div className="text-xs text-gray-500 mt-1">Go live today, not weeks</div>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <Sparkles size={32} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-[#0E163B] mb-2">100%</div>
              <div className="text-gray-600 font-medium">Customizable</div>
              <div className="text-xs text-gray-500 mt-1">Adapt to your workflow</div>
            </div>
            <div className="text-center group">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-gradient-to-br from-[#FF5A3D] to-[#FF8C42] rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <TrendingUp size={32} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-[#0E163B] mb-2">{formatPrice(5)}</div>
              <div className="text-gray-600 font-medium">14-Day Trial</div>
              <div className="text-xs text-gray-500 mt-1">No credit card required</div>
            </div>
          </div>
        </div>
      </section>

      {/* Browse by Business Type */}
      <section className="py-16 px-4 bg-white border-b">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E163B] mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Browse by Business Type
            </h2>
            <p className="text-xl text-gray-600">
              Find solutions designed for how your business operates
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/industries/home-services" onClick={scrollToTop}>
              <Card className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border-2 hover:border-[#FF5A3D]">
                <CardContent className="p-6 text-center">
                  <Home size={48} className="mx-auto mb-4 text-[#FF5A3D]" />
                  <h3 className="text-xl font-bold text-[#0E163B] mb-2">Home Services</h3>
                  <p className="text-gray-600 mb-4">On-site trades & services</p>
                  <div className="flex items-center justify-center text-[#FF5A3D] font-semibold">
                    View All <ArrowRight size={18} className="ml-2" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/industries/high-street" onClick={scrollToTop}>
              <Card className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border-2 hover:border-[#FF5A3D]">
                <CardContent className="p-6 text-center">
                  <Store size={48} className="mx-auto mb-4 text-[#FF5A3D]" />
                  <h3 className="text-xl font-bold text-[#0E163B] mb-2">High Street</h3>
                  <p className="text-gray-600 mb-4">Walk-in retail & services</p>
                  <div className="flex items-center justify-center text-[#FF5A3D] font-semibold">
                    View All <ArrowRight size={18} className="ml-2" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/industries/appointment-based" onClick={scrollToTop}>
              <Card className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border-2 hover:border-[#FF5A3D]">
                <CardContent className="p-6 text-center">
                  <Calendar size={48} className="mx-auto mb-4 text-[#FF5A3D]" />
                  <h3 className="text-xl font-bold text-[#0E163B] mb-2">Appointment-Based</h3>
                  <p className="text-gray-600 mb-4">Scheduled consultations</p>
                  <div className="flex items-center justify-center text-[#FF5A3D] font-semibold">
                    View All <ArrowRight size={18} className="ml-2" />
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/industries/professional-services" onClick={scrollToTop}>
              <Card className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border-2 hover:border-[#FF5A3D]">
                <CardContent className="p-6 text-center">
                  <Briefcase size={48} className="mx-auto mb-4 text-[#FF5A3D]" />
                  <h3 className="text-xl font-bold text-[#0E163B] mb-2">Professional Services</h3>
                  <p className="text-gray-600 mb-4">B2B & consulting</p>
                  <div className="flex items-center justify-center text-[#FF5A3D] font-semibold">
                    View All <ArrowRight size={18} className="ml-2" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Choose Your Industry
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Each industry page includes a complete snapshot with pre-built templates, automation workflows, 
              and best practices tailored to your business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry) => {
              const Icon = industry.icon;
              return (
                <Card 
                  key={industry.id}
                  className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer group"
                >
                  <CardContent className="p-6">
                    <Link to={industry.route} onClick={scrollToTop}>
                      <div className={`${industry.color} w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <Icon size={28} className="text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0E163B] mb-3 group-hover:text-[#FF5A3D] transition-colors">
                        {industry.name}
                      </h3>
                      <p className="text-gray-600 mb-4 leading-relaxed">
                        {industry.description}
                      </p>
                      <div className="flex items-center text-[#FF5A3D] font-semibold group-hover:translate-x-2 transition-transform duration-300">
                        Learn More <ArrowRight size={18} className="ml-2" />
                      </div>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-[#0E163B] mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              What's Included in Each Industry Snapshot?
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to start capturing and converting leads in minutes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-[#FF5A3D]/10 to-[#FF8C42]/10 p-8 rounded-2xl">
              <div className="text-3xl mb-4">📱</div>
              <h3 className="text-xl font-bold text-[#0E163B] mb-3">Pre-Built Templates</h3>
              <p className="text-gray-600">
                Landing pages, forms, and email templates designed specifically for your industry
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-8 rounded-2xl">
              <div className="text-3xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-[#0E163B] mb-3">Automation Workflows</h3>
              <p className="text-gray-600">
                Pre-configured follow-up sequences, appointment reminders, and nurture campaigns
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl">
              <div className="text-3xl mb-4">🎯</div>
              <h3 className="text-xl font-bold text-[#0E163B] mb-3">Lead Capture Forms</h3>
              <p className="text-gray-600">
                Optimized forms and chatbots designed to capture leads specific to your services
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-2xl">
              <div className="text-3xl mb-4">📧</div>
              <h3 className="text-xl font-bold text-[#0E163B] mb-3">Email & SMS Templates</h3>
              <p className="text-gray-600">
                Proven messaging templates that get responses and book appointments
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-8 rounded-2xl">
              <div className="text-3xl mb-4">📅</div>
              <h3 className="text-xl font-bold text-[#0E163B] mb-3">Calendar Integration</h3>
              <p className="text-gray-600">
                Online booking with automatic reminders to reduce no-shows by 40%
              </p>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-8 rounded-2xl">
              <div className="text-3xl mb-4">⭐</div>
              <h3 className="text-xl font-bold text-[#0E163B] mb-3">Review Automation</h3>
              <p className="text-gray-600">
                Automatic review requests sent to happy customers at the perfect moment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-[#FF5A3D] to-[#FF8C42] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Dominate Your Local Market?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Pick your industry above and see how our pre-built system can transform your business in just 14 days
          </p>
          
          <Button 
            asChild
            className="bg-white text-[#FF5A3D] hover:bg-gray-100 font-bold text-xl px-10 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-2xl"
          >
            <Link to="/pricing">
              Start 14-Day Trial for {formatPrice(5)}
            </Link>
          </Button>
          
          <div className="flex flex-wrap justify-center gap-8 mt-8 text-sm opacity-75">
            <span>✓ Credit card required</span>
            <span>✓ Cancel anytime</span>
            <span>✓ Setup in 5 minutes</span>
            <span>✓ Industry-specific templates</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default IndustriesPage;
